import {
  ApolloClient,
  CombinedGraphQLErrors,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  ServerError,
  split,
} from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { Observable } from 'rxjs';

import generatedFragmentMatcher from './generated/fragment-matcher';
import { getSessionTokens } from './utils/Amplify';

const httpLink = new HttpLink({ uri: process.env.REACT_APP_GRAPHQL_URI });

let authToken = undefined;

const link = split(() => !!authToken, httpLink);

// `handledLocally`: the request shows its own error, so only the sign-out applies
type handleErrorType = (message: string, code?: string, handledLocally?: boolean) => any;

const createAuthLink = () =>
  new SetContextLink(({ headers }) => ({
    headers: {
      ...headers,
      authorization: authToken,
    },
  }));

const refreshToken = async () => {
  authToken = (await getSessionTokens({ forceRefresh: true })).idToken;
};

const createErrorLink = (handleError: handleErrorType) => {
  return new ErrorLink(({ error, operation, forward }): Observable<FetchResult<Record<string, any>>> | void => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, extensions }) => {
        const code = extensions?.code as string;
        const handledLocally = !!operation.getContext().errorHandledLocally;
        // A gone session still signs the user out for requests that handle their own errors
        if (!handledLocally || code === 'UNAUTHENTICATED') {
          handleError(message, code, handledLocally);
        }
      });
    }

    if ((error as ServerError)?.statusCode === 401) {
      return new Observable<FetchResult<Record<string, any>>>((observer) => {
        void (async () => {
          try {
            await refreshToken();
          } catch (refreshError) {
            // Fail the request instead of leaving it loading forever
            observer.error(refreshError);
            return;
          }

          // Retry the failed request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };

          forward(operation).subscribe(subscriber);
        })();
      });
    }
    return;
  });
};

export const createClient = (token: string | null, onError: handleErrorType) => {
  authToken = token;
  const errorLink = createErrorLink(onError);
  const links = [errorLink];
  if (authToken) {
    links.push(createAuthLink());
  }
  links.push(link);

  return new ApolloClient({
    link: from(links),

    cache: new InMemoryCache({
      possibleTypes: generatedFragmentMatcher.possibleTypes,
      typePolicies: {
        Query: {
          fields: {
            pulseNodes: {
              keyArgs: ['parentPaths', 'searchQuery'],
              merge(existing = { nodes: [] }, incoming, { args }) {
                if (args?.after) {
                  return {
                    ...incoming,
                    nodes: [...existing.nodes, ...incoming.nodes],
                  };
                }

                return incoming;
              },
            },
          },
        },
        Audience: {
          fields: {
            pixelSettings: {
              merge: true,
            },
          },
        },
      },
    }),

    devtools: { enabled: process.env.REACT_APP_APOLLO_DEVTOOLS === 'true' },
  });
};
